import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, useToast } from '@/components/common';
import { errorMessage } from '@/lib/api-client';
import type { Clinic, WorkingDay } from '@/types/api';
import { weekdayNames } from '../../chart/components';
import { settingsApi } from '../api';

type Row = { open: boolean; start: string; end: string };

const DEFAULT_ROW: Row = { open: false, start: '08:00', end: '13:00' };
const HOURS = Array.from({ length: 25 }, (_, h) => ({
  value: h,
  label: `${String(h).padStart(2, '0')}:00`,
}));

function toRows(hours: WorkingDay[]): Row[] {
  return Array.from({ length: 7 }, (_, i) => {
    const day = hours.find((d) => d.day === i + 1);
    return day ? { open: true, start: day.start, end: day.end } : DEFAULT_ROW;
  });
}

function toHours(rows: Row[]): WorkingDay[] {
  return rows.flatMap((r, i) => (r.open ? [{ day: i + 1, start: r.start, end: r.end }] : []));
}

/** Opening hours per weekday, the hours the calendar shows and the weekend toggle. */
export function WorkingHours({
  clinic,
  onChange,
}: {
  clinic: Clinic;
  onChange: (c: Clinic) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const days = weekdayNames();
  const [rows, setRows] = useState(() => toRows(clinic.workingHours));
  const [range, setRange] = useState({ start: clinic.calendarStart, end: clinic.calendarEnd });
  const [showWeekend, setShowWeekend] = useState(clinic.showWeekend);
  const [busy, setBusy] = useState(false);

  const badDay = rows.findIndex((r) => r.open && (!(r.start && r.end) || r.start >= r.end));
  const badRange = range.start >= range.end;

  const update = (i: number, patch: Partial<Row>) =>
    setRows((list) => list.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      onChange(
        await settingsApi.updateClinic({
          workingHours: toHours(rows),
          calendarStart: range.start,
          calendarEnd: range.end,
          showWeekend,
        })
      );
      toast.show(t('settings.saved'));
    } catch (err) {
      toast.show(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="stack" onSubmit={submit}>
      <section className="card">
        <div className="card__head">
          <h2>{t('settings.hours.title')}</h2>
        </div>
        <p className="small muted">{t('settings.hours.hint')}</p>
        <ul className="list hours">
          {rows.map((r, i) => (
            <li key={days[i]} className="hours__row">
              <label className="check hours__day">
                <input
                  type="checkbox"
                  checked={r.open}
                  onChange={(e) => update(i, { open: e.target.checked })}
                />
                {days[i]}
              </label>
              {r.open ? (
                <span className="hours__times">
                  <input
                    type="time"
                    className="input"
                    step={1800}
                    aria-label={t('settings.hours.from', { day: days[i] })}
                    value={r.start}
                    onChange={(e) => update(i, { start: e.target.value })}
                  />
                  <span aria-hidden="true">–</span>
                  <input
                    type="time"
                    className="input"
                    step={1800}
                    aria-label={t('settings.hours.to', { day: days[i] })}
                    value={r.end}
                    onChange={(e) => update(i, { end: e.target.value })}
                  />
                </span>
              ) : (
                <span className="muted small">{t('settings.hours.closed')}</span>
              )}
            </li>
          ))}
        </ul>
        {badDay >= 0 && (
          <p className="small error-text">{t('settings.hours.badDay', { day: days[badDay] })}</p>
        )}
      </section>

      <section className="card">
        <div className="card__head">
          <h2>{t('settings.calendar.title')}</h2>
        </div>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="cal-start">{t('settings.calendar.start')}</label>
            <Dropdown
              id="cal-start"
              value={range.start}
              options={HOURS.slice(0, 24)}
              onChange={(start) => setRange((r) => ({ ...r, start }))}
            />
          </div>
          <div className="field">
            <label htmlFor="cal-end">{t('settings.calendar.end')}</label>
            <Dropdown
              id="cal-end"
              value={range.end}
              options={HOURS.slice(1)}
              onChange={(end) => setRange((r) => ({ ...r, end }))}
            />
          </div>
        </div>
        {badRange && <p className="small error-text">{t('settings.calendar.badRange')}</p>}
        <label className="check" style={{ marginTop: 12 }}>
          <input
            type="checkbox"
            checked={showWeekend}
            onChange={(e) => setShowWeekend(e.target.checked)}
          />
          {t('settings.calendar.weekend')}
        </label>
      </section>

      <div className="row">
        <button type="submit" className="btn btn--dark" disabled={busy || badDay >= 0 || badRange}>
          {t('common.save')}
        </button>
      </div>
    </form>
  );
}
